import { useEffect, useState } from "react";

export function useReviewPlanVisitSelection(
    allVisitIds: string[],
    visitToUserId: Map<string, string | null>,
    currentUserId: string | undefined,
) {
    const computeDefaultSelection = (): string[] => {
        if (currentUserId === undefined) return [];
        return allVisitIds.filter(id => visitToUserId.get(id) === currentUserId);
    };

    const [selectedVisitIds, setSelectedVisitIds] = useState<string[]>(computeDefaultSelection);

    const allVisitIdsKey = allVisitIds.join("|");
    const currentUserIdKey = currentUserId ?? "";
    // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the set of visit ids or current user changes
    useEffect(() => {
        setSelectedVisitIds(computeDefaultSelection());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [allVisitIdsKey, currentUserIdKey]);

    const isAllSelected = allVisitIds.length > 0 && selectedVisitIds.length === allVisitIds.length;

    const toggleVisit = (visitId: string) => {
        setSelectedVisitIds(previous => {
            if(previous.includes(visitId)) {
                return previous.filter(x => x !== visitId)
            } else {
                return [...previous, visitId]
            }
        });
    };

    const toggleAll = () => {
        setSelectedVisitIds(isAllSelected ? [] : allVisitIds);
    };

    const toggleUser = (userId: string | null) => {
        setSelectedVisitIds(previous => {
            const userVisitIds = allVisitIds.filter(id => visitToUserId.get(id) === userId);
            const allUserSelected = userVisitIds.every(id => previous.includes(id));
            if (allUserSelected) {
                return previous.filter(x => !userVisitIds.includes(x))
            } else {
                return [...new Set([...previous, ...userVisitIds])]
            }
        });
    };

    const isUserFullySelected = (userId: string | null): boolean => {
        const userVisitIds = allVisitIds.filter(id => visitToUserId.get(id) === userId);
        return userVisitIds.length > 0 && userVisitIds.every(id => selectedVisitIds.includes(id));
    };

    const isUserPartiallySelected = (userId: string | null): boolean => {
        const userVisitIds = allVisitIds.filter(id => visitToUserId.get(id) === userId);
        const selectedCount = userVisitIds.filter(id => selectedVisitIds.includes(id)).length;
        return selectedCount > 0 && selectedCount < userVisitIds.length;
    };

    const getUserSelectedCount = (userId: string | null): number => {
        return allVisitIds.filter(id => visitToUserId.get(id) === userId && selectedVisitIds.includes(id)).length;
    };

    return {
        selectedVisitIds,
        isAllSelected,
        toggleVisit,
        toggleAll,
        toggleUser,
        isUserFullySelected,
        isUserPartiallySelected,
        getUserSelectedCount,
    };
}
